/**
 * Chip — small rounded inline tag primitive (Block 6.F.1; per design
 * plan §3.2). Ported .jsx → .tsx at UX-8 PR-B (typed props, zero
 * behavior change).
 *
 * 8 variant names, 6 visual treatments, every value a brand token
 * (clinical identity set, UX-13 direction; Amanda ruled 2026-07-02):
 *   - service        sage-50 / sage-600         (subcategories + payment-types)
 *   - specialty      warm-100 / warm-700        (specialties; warm-700 for AA at chip size)
 *   - neutral        sand / ink-soft            (facts: areas, counts, "No" Medicaid)
 *   - medicaid-yes   tick-affirm / affirm-deep  (Medicaid: Yes · Straight)
 *   - medicaid-tbd   gold-soft / caution-deep   (Medicaid: Type TBD / Spend Down)
 *   - dementia       heather / heather-deep     (Memory Care; the lock icon carries recognition)
 *   - service-model  renders neutral            (a fact, not a status; reads in words)
 *   - restrictions   renders neutral            (same)
 *
 * The Tailwind-default hues (green/amber/blue/cyan/rose) retired with the
 * UX-13 direction: hue only where hue is signal. Variant names are stable
 * so consumers need no edits; semantic renames (affirm/caution/memory)
 * arrive with the UX-13 Phase 2 conversion.
 *
 * Mirrors peace-hub ResourceCard.jsx inline span styles, extracted for
 * reuse across 6.F.2-5 ports + cleaner test coverage.
 *
 * THE DESIGN LAW (record-page design review, 2026-07-06; extended Soak #2):
 *   Color is meaning. Weight is importance. Emptiness is quiet. A section
 *   earns its card by having content, and gold means one thing.
 *
 * TWO KINDS OF HUE — do not mix them:
 *   1. SIGNAL registers (carry STATE meaning; the ONLY colors that mean a
 *      state). FOUR since the founder ruling of 2026-09-01:
 *        - affirm (sage-green)  Active / On file — positive, settled, done.
 *        - caution (gold)       ONE thing only: awaiting your confirm.
 *        - risk (pepper)        risk / attention / urgent clinical signal
 *                               (pepper-deep on pepper-soft = 5.0:1, AA).
 *        - danger (--color-danger, a true red)  MONEY MAY ALREADY BE
 *                               WRONG. Ruled 2026-09-01, verbatim: "you
 *                               can add a danger color" — the billing
 *                               surfaces had one tone for two unlike
 *                               things and the worse one was losing.
 *                               It is NOT a louder risk. Risk says
 *                               "something needs you"; danger says "harm
 *                               may already have happened, and the
 *                               obvious next click can make it worse":
 *                               an invoice at Intuit whose total
 *                               disagrees with the total Olive froze, or
 *                               a voided invoice whose QuickBooks copy
 *                               is still open with a payment applied.
 *                               A FIXABLE refusal ("nothing was sent,
 *                               map the rate band") stays pepper-deep.
 *                               Spend it sparingly or it becomes the new
 *                               pepper. The token and its measured
 *                               ratios live in src/index.css. There is
 *                               deliberately NO Chip variant yet: the
 *                               surfaces that carry this tone render
 *                               text, not fills. Add one when something
 *                               actually wants a filled chip in it.
 *   2. CATEGORY / WAYFINDING hues (carry NO state meaning — they label a
 *      KIND of thing). service (sage), specialty (warm), memory (heather),
 *      and the Soak #2 additions mist (warm pale blue) + amber (deeper
 *      champagne) belong here. A category tag NEVER borrows a signal token:
 *      the Latest-Activity color tags must not use affirm/caution. `spice`
 *      (pepper-deep on pepper-soft, the same pair `risk` uses) also
 *      belongs here — it names the SPICE service line, never a clinical
 *      risk, so it stays its own arm rather than reusing `risk`'s name.
 *   neutral (sand) is every other plain fact — reads in words, not hue.
 *   3. The STANCE register: stance-open (forest, cream words),
 *      stance-talk-to-me (the gold, forest-700 words) and stance-not-open
 *      (the faded terracotta, ink words): the availability switch's three
 *      looks. These three fills mean a care manager's stance on new
 *      clients, set by the office, and nothing else; the gold here is not
 *      the caution signal. on-leave is the colourless grey beside them.
 */

import { type ComponentPropsWithRef, type ReactNode } from 'react';

export type ChipVariant =
  | 'service'
  | 'specialty'
  | 'neutral'
  // UX-13 S0 semantic names — the canonical trio going forward.
  | 'affirm'
  | 'caution'
  | 'memory'
  // Record-page design review (2026-07-06): risk/urgent clinical signal.
  | 'risk'
  // Strip v3 (founder ruled 2026-08-25): the hollow vessel — an ABSENT
  // value ("Not recorded"). The one variant with a border and no fill:
  // absence is visibly "nothing here yet", never a filled state. Copy is
  // always a noun phrase; every verb lives outside the chip.
  | 'absent'
  // The absent vessel in the clinical-warning ink: an absence that is
  // itself a safety fact (a SPICE-only client with nobody to call — the
  // therapist visits alone and must be able to reach someone). Same
  // hollow, hairline shape as `absent`; only the ink changes, and the
  // ink alone is the signal. Not pepper: pepper marks the SPICE face,
  // and this is a clinical safety fact, not a brand accent.
  | 'clinical-warn'
  // Soak #2 CATEGORY / WAYFINDING hues (NOT register-signals — no state
  // meaning): the Latest-Activity color-tag set. `mist` = the founder's
  // 6th hue (warm pale blue-grey); `amber` = a deeper champagne than
  // `specialty`, shifted so Med & allergy never reads like risk/pepper.
  | 'mist'
  | 'amber'
  // The SPICE brand accent: names the SPICE service line on a SPICE face
  // (the record header's SPICE-only engagement chip, a session note's
  // kind chip in the feed) — a category label, not the clinical `risk`
  // signal, even though it draws the same pepper-deep-on-pepper-soft pair.
  | 'spice'
  // The stance register (see CHIP_VARIANT_CLASSES).
  | 'stance-open'
  | 'stance-talk-to-me'
  | 'stance-not-open'
  | 'on-leave'
  // Deprecated legacy names (compile through the rebuild; slices convert
  // incrementally): medicaid-yes→affirm, medicaid-tbd→caution,
  // dementia→memory, service-model/restrictions→neutral.
  | 'medicaid-yes'
  | 'medicaid-tbd'
  | 'dementia'
  | 'service-model'
  | 'restrictions';

// Exported for the Editable* chip renderers (UX-13 S3): they previously
// carried hand-copied "inline mirror" switches that drifted to the retired
// Tailwind hues when this map moved to the brand identity set — one map,
// no mirrors.
export const CHIP_VARIANT_CLASSES: Record<ChipVariant, string> = {
  service: 'bg-sage-50 text-sage-600',
  // warm-700, not warm-600: warm-600 on warm-100 is 4.16:1 and fails AA
  // at chip size (house rule per the UX-13 direction: warm-700 is the
  // small-text gold).
  specialty: 'bg-warm-100 text-warm-700',
  neutral: 'bg-sand text-ink-soft',
  // Clinical identity set (UX-13 direction; Amanda ruled 2026-07-02):
  // hue only where hue is signal. Coverage keeps color in the brand's
  // own green/gold; Memory Care takes the heather family (the lock icon
  // carries recognition through the hue change). service-model and
  // restrictions are facts, not statuses: they read in words on sand.
  // Variant NAMES are unchanged so no consumer edits are needed; the
  // semantic renames (affirm/caution/memory) arrive with UX-13 Phase 2.
  affirm: 'bg-tick-affirm text-affirm-deep',
  caution: 'bg-gold-soft text-caution-deep',
  memory: 'bg-heather text-heather-deep',
  // Risk/urgent — the pepper (terracotta) family; pepper-deep on pepper-soft
  // is 5.0:1, AA at chip size. Gold does not carry risk: in the signal
  // registers it means only awaiting-confirm. The stance register
  // below is a register of its own.
  risk: 'bg-pepper-soft text-pepper-deep',
  // Strip v3: hollow — hairline border, transparent, muted ink. The first
  // bordered variant; deliberately fainter than any interactive border
  // (16% hairline vs Button's solid sage-300) so it cannot read as a
  // control.
  absent: 'bg-transparent border border-sage-800/[16%] text-ink-soft',
  // The absent vessel, clinical-warning ink (10.20:1 on paper, 9.55:1 on
  // cream — src/index.css). Border and fill are `absent`'s exactly: the
  // reader sees the same "nothing here yet" shape, in the ink that says
  // this particular nothing is unsafe.
  'clinical-warn': 'bg-transparent border border-sage-800/[16%] text-clinical-warn',
  // Soak #2 CATEGORY hues (no state meaning; the Latest-Activity tag set).
  // mist = the warm pale blue-grey 6th hue; mist-deep on mist is 6.53:1.
  // amber = a deeper champagne than `specialty` (warm-200, not warm-100), so
  // Med & allergy is clearly separable from the pepper risk tag; warm-700 on
  // warm-200 is 4.98:1 (AA at chip size).
  mist: 'bg-mist text-mist-deep',
  amber: 'bg-warm-200 text-warm-700',
  // The SPICE brand accent (founder ruling: pepper is the accent role on a
  // SPICE face, forest stays structural everywhere else). Same pair as
  // `risk` (pepper-deep on pepper-soft, 5.0:1 AA at chip size) under its
  // own name: this marks SPICE identity, never a clinical risk signal.
  spice: 'bg-pepper-soft text-pepper-deep',
  // The stance register: a care manager's stance on new clients, set by
  // the office, and nothing else. Each is the stance's chip classes in
  // src/lib/capacity.ts: the availability switch's look for the stance.
  // On leave is its own colourless grey, never a stance's colour.
  'stance-open': 'bg-stance-open text-cream',
  'stance-talk-to-me': 'bg-stance-talk-to-me text-forest-700',
  'stance-not-open': 'bg-stance-not-open text-ink',
  'on-leave': 'bg-on-leave text-ink',
  'medicaid-yes': 'bg-tick-affirm text-affirm-deep',
  'medicaid-tbd': 'bg-gold-soft text-caution-deep',
  dementia: 'bg-heather text-heather-deep',
  'service-model': 'bg-sand text-ink-soft',
  restrictions: 'bg-sand text-ink-soft',
};

// Geometry ruling (Amanda 2026-07-02, UX-13 direction): chips take the
// tight radius; the full pill retires from interactive/tag elements.
// text in REM (true-zoom ruling 2026-08-25: primitives scale with the
// reader's text size too — the scale slice's exemption is revoked).
const BASE = 'px-2 py-0.5 rounded-tight text-[0.6875rem] font-medium inline-block';

export interface ChipProps extends ComponentPropsWithRef<'span'> {
  variant?: ChipVariant;
}

export function Chip({
  variant = 'neutral',
  className = '',
  children,
  ...rest
}: ChipProps): ReactNode {
  const variantCls = CHIP_VARIANT_CLASSES[variant] ?? CHIP_VARIANT_CLASSES.neutral;
  const cls = [BASE, variantCls, className].filter(Boolean).join(' ');
  return (
    <span className={cls} {...rest}>
      {children}
    </span>
  );
}
